import "primereact/resources/themes/mdc-light-deeppurple/theme.css" //import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css"; //core css
import "primeicons/primeicons.css"; //icons
import 'primeflex/primeflex.css';
import Cookies from 'js-cookie';
import React, {useEffect, useRef, useState, useCallback, useMemo, useContext, createContext} from 'react';

import MainPage from "./components/MainPage";
import Callback from "./components/Callback";
import {BrowserRouter, Route, Routes} from "react-router";
import {UserService} from "./services/UserService";
import {AMService} from "./services/AMService";
import ResetPassword from "./components/ResetPassword";

export const AuthContext = createContext(null);
export const ParamsContext = createContext({spotifyInitializerPlaylist: ''});

export default function App() {
    const [currentUser, setCurrentUser] = useState(null);
    const [tokenExpiry, setTokenExpiry] = useState(null);
    const [refreshTimer, setRefreshTimer] = useState(null);
    const [musicKitLoaded, setMusicKitLoaded] = useState(false);
    const [devToken, setDevToken] = useState(''); // for AM
    const [devTokenTimer, setDevTokenTimer] = useState(null);
    const [isAMauthorized, setIsAMauthorized] = useState(false);
    const userservice = new UserService();
    const amservice = new AMService();

    // for apple music load MusicKit
    useEffect(() => {
        const script = document.createElement('script');
        script.src = "https://js-cdn.music.apple.com/musickit/v3/musickit.js";
        script.async = true;
        document.body.appendChild(script);
        document.addEventListener("musickitloaded", () => {setMusicKitLoaded(true);});

        amDevToken(); // refresh is timed

        return () => {
            document.body.removeChild(script);
        }
    }, []); // this should only run once

    useEffect(() => {
        if(musicKitLoaded && devToken !== '' ) {
            setMusicInstance();
        }
    }, [devToken, musicKitLoaded]);


    // for my app
    useEffect(() => {
        if(tokenExpiry == null){
            refreshToken(); // refresh is timed
            // setInterval( ''' call same code repeatedly
        }
        let accessToken = getAccessToken();
        if(accessToken !== null && accessToken !== undefined ) {
            if(currentUser === null){
                // reauthenticate
                userservice.getCurrentUser(accessToken).then(response => {
                    if(response.status === 200) {
                        response.json().then(data => {
                            setCurrentUser(data);
                            return true;
                        })
                    } else {
                        logout(); // try reauthenticating? done withing getAccessToken()
                        return false;
                    }
                })
            }
        }
    }, [tokenExpiry])

    async function refreshToken() {
        let token = localStorage.getItem('refresh_token')
        if(token !== null) {
            return (userservice.getTokenFromRefreshToken(token).then(resp => {
                if (resp.status === 200) {
                    resp.json().then(data => {
                        storeToken(data);
                        return true;
                    })
                } else {
                    if (resp.status === 403) {
                        setCurrentUser(null);
                        sessionStorage.removeItem('access_token');
                    } else {
                        logout();
                        return false;
                    }
                }
            }).finally(() => {
                setRefreshTimer(setTimeout(refreshToken, 57 * 60000)); // 1min to test
            }));
        }
    }

    async function amDevToken() {
        return amservice.getDevToken().then(res =>
        {
            setDevToken(res);
        }).finally(() => {
            setDevTokenTimer(setTimeout(amDevToken, 58 * 60000));
        });
    }


    async function setMusicInstance() {
        const resp = await window.MusicKit.configure({
            developerToken: devToken,
            app: {
                name: 'media.ch.laramon',
                build: '2025.11.06',
                //icon: 'media/myappicon.jpg' needs to be a href
            },
        }).then(
            data => {
                if(data.isAuthorized){
                    setIsAMauthorized(true);
                } else {
                    setIsAMauthorized(false);
                }
            }
        )
    }

    const login = useCallback((response) => {
        let token = response?.token;
        storeToken(token);

        if(response !== null && response.user === null) {
            try{
                userservice.getCurrentUser(token?.access_token).then(response => {
                    if(response.status === 200) {
                        response.json().then(data => {
                            setCurrentUser(data);
                            return true;
                        })
                    } else {
                        logout();
                        return false;
                    }
                })
            } catch(err) {
                logout();
                return false;
            }
        }
        else if (response === null){
            logout();
            return false;
        }
        else
        {
            setCurrentUser(response.user);
            return true;
        }

        }, []);


    function logout() {
        sessionStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');  // find the correct solution for this chaos
        setCurrentUser(null);
        setTokenExpiry(null);
        clearTimeout(refreshTimer);
        setRefreshTimer(null);
        return true
    }

    function logoutAM() {
        window.MusicKit.getInstance().unauthorize();
        setIsAMauthorized(false);
        // we still want the AM token to be ready at all times .. so keeping the MusicKit configuration up to date...
        // is that unnecessary??? should we just configure it, when opening the dialog?
    }

    function storeToken(token) {
        if (token === null){
            logout()
            return false
        } else {
            sessionStorage.setItem('access_token', token?.access_token);
            localStorage.setItem('refresh_token', token?.refresh_token);
            setTokenExpiry(new Date().getTime() + 2*60000);  /// check if this is actually used... the refresh is done via refreshTimer now
            // Cookies.set('token', token, {
            //     expires: 7,
            //     secure: true,
            //     sameSite: 'strict',
            //     httpOnly: true,
            // });
            return true
        }
    }

     function getAccessToken(){
        return sessionStorage.getItem('access_token');
        // is this called, when promise fulfilled? or earlier?
    }

    function isAuthenticated () {
        return(currentUser !== null && currentUser !== undefined);
    }

    const contextValue = useMemo(()=> ({
        currentUser,
        login,
        logout,
        getAccessToken,
        isAuthenticated,
        musicKitLoaded,
        isAMauthorized,
        setIsAMauthorized,
        logoutAM,
    }), [currentUser, isAuthenticated, musicKitLoaded, isAMauthorized]); //login, logout, getAccessToken,

    return(
        <BrowserRouter>
            <AuthContext value={contextValue}>
                <Routes>
                    <Route path="/" element={<MainPage/>}/>
                    <Route path="/playlist/:spotifyInitializerPlaylist" element={<MainPage/>}/>
                    <Route path="/editor/:editorInitializerPlaylist" element={<MainPage/>}/>
                    <Route path="/callback" element={<Callback/>}/>
                    <Route path="/resetpassword" element={<ResetPassword/>}/>
                    <Route path="/spotifyusers" element={<MainPage/>}/>
                </Routes>
            </AuthContext>
        </BrowserRouter>
    )
}
	